// Minimal, dependency-free JWT inspection for the access token.
//
// We only need the `exp` claim to decide whether to refresh proactively. We do
// NOT verify the signature here — that's the backend's job; this is purely a
// client-side hint to avoid sending an already-expired token to the optional-auth
// analyze endpoints (which silently treat an expired token as anonymous instead
// of returning a 401, so the reactive refresh in restClient never fires).

/**
 * Decode a JWT's `exp` claim (UNIX seconds). Returns `null` when the token is
 * absent, not a three-part JWT, undecodable, or carries no numeric `exp` — e.g.
 * the build-time WS_TOKEN, which isn't a refreshable user token.
 */
export function getAccessTokenExpiry(token: string | null): number | null {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    // base64url → base64, then atob. Pad to a multiple of 4 for atob.
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64.padEnd(b64.length + ((4 - (b64.length % 4)) % 4), '=');
    const payload = JSON.parse(atob(padded)) as { exp?: unknown };
    return typeof payload.exp === 'number' ? payload.exp : null;
  } catch {
    return null;
  }
}

/**
 * True when the access token is expired or within `skewSeconds` of expiring.
 * An undecodable token (no readable `exp`) is treated as NOT expired so the
 * historical WS_TOKEN fallback keeps working without a refresh attempt.
 */
export function isAccessTokenExpired(token: string | null, skewSeconds = 30): boolean {
  const exp = getAccessTokenExpiry(token);
  if (exp === null) return false;
  return Date.now() / 1000 >= exp - skewSeconds;
}
